import React, { useState } from 'react';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import './PedidosPage.css';

const TIPO_COMPRA_OPTIONS = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' }
];

const LUGAR_ENTREGA_OPTIONS = [
  'UG', 'CAN', 'CAS', 'CALO', 'VECINDARIO', 'RAMBLAS', 'OTRO'
];

const PRIORIDAD_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'urgente', label: 'Urgente' }
];

const initialLinea = () => ({
  id: Date.now(),
  articulo: '',
  cantidad: 1,
  uso: ''
});

const PedidosPage = ({ darkMode, toggleDarkMode }) => {
  const [tipoCompra, setTipoCompra] = useState('hardware');
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [lineas, setLineas] = useState([initialLinea()]);
  const [lugarEntrega, setLugarEntrega] = useState('UG');
  const [prioridad, setPrioridad] = useState('normal');
  const [motivoUrgencia, setMotivoUrgencia] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);

  const addLinea = () => {
    setLineas(prev => [...prev, initialLinea()]);
  };

  const removeLinea = (id) => {
    if (lineas.length <= 1) return;
    setLineas(prev => prev.filter(l => l.id !== id));
  };

  const updateLinea = (id, field, value) => {
    setLineas(prev =>
      prev.map(l =>
        l.id === id ? { ...l, [field]: value } : l
      )
    );
  };

  const validate = () => {
    const err = {};
    if (!nombre.trim()) err.nombre = 'Indica tu nombre';
    if (!email.trim()) err.email = 'Indica tu email';
    if (!telefono.trim()) err.telefono = 'Indica un teléfono de contacto';
    const hasArticulo = lineas.some(l => l.articulo.trim());
    if (!hasArticulo) err.lineas = 'Añade al menos un artículo';
    if (prioridad === 'urgente' && !motivoUrgencia.trim()) {
      err.motivoUrgencia = 'Indica el motivo de la urgencia';
    }
    setErrores(err);
    return Object.keys(err).length === 0;
  };

  const buildEmailBody = (payload) => {
    const tipoLabel = payload.tipoCompra === 'software' ? 'Software' : 'Hardware';
    const prioridadLabel = payload.prioridad === 'urgente' ? 'Urgente' : 'Normal';
    let body = 'Nuevo pedido desde la web (Recepción de pedidos)\n';
    body += '='.repeat(50) + '\n\n';
    body += 'CONTACTO\n';
    body += `Nombre:   ${payload.nombre}\n`;
    body += `Email:    ${payload.email}\n`;
    body += `Teléfono: ${payload.telefono}\n\n`;
    body += `Tipo de compra: ${tipoLabel}\n`;
    body += `Lugar de entrega: ${payload.lugarEntrega}\n`;
    body += `Prioridad: ${prioridadLabel}\n`;
    if (payload.prioridad === 'urgente' && payload.motivoUrgencia) {
      body += `Motivo urgencia: ${payload.motivoUrgencia}\n`;
    }
    body += '\nARTÍCULOS\n';
    body += '-'.repeat(50) + '\n';
    payload.lineas.forEach((l, i) => {
      body += `${i + 1}. ${l.articulo} (Cantidad: ${l.cantidad})\n`;
      if (l.uso) body += `   Uso: ${l.uso}\n`;
    });
    body += '\n';
    if (payload.observaciones) {
      body += 'OBSERVACIONES\n';
      body += '-'.repeat(50) + '\n';
      body += payload.observaciones + '\n';
    }
    return body;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      tipoCompra,
      nombre: nombre.trim(),
      email: email.trim(),
      telefono: telefono.trim(),
      lineas: lineas.filter(l => l.articulo.trim()).map(l => ({
        articulo: l.articulo.trim(),
        cantidad: Number(l.cantidad) || 1,
        uso: l.uso.trim()
      })),
      lugarEntrega,
      prioridad,
      motivoUrgencia: prioridad === 'urgente' ? motivoUrgencia.trim() : '',
      observaciones: observaciones.trim()
    };

    const webhookUrl = process.env.REACT_APP_PEDIDOS_WEBHOOK_URL;
    const apiUrl = process.env.REACT_APP_PEDIDOS_EMAIL_URL || '/api/send-pedido.php';
    const useWebhook = !!webhookUrl;

    setErrorEnvio(null);
    setEnviando(true);

    try {
      let res;
      if (useWebhook) {
        const tipoLabel = payload.tipoCompra === 'software' ? 'Software' : 'Hardware';
        const subject = `[E-ASY Pedidos] ${tipoLabel} - ${payload.nombre} - ${payload.lugarEntrega}`;
        const text = buildEmailBody(payload);
        const toEmail = process.env.REACT_APP_PEDIDOS_EMAIL_TO || 'comercial@e-asy.es';
        res = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: toEmail,
            cc: '',
            cc1: '',
            cc2: '',
            cc3: '',
            subject,
            text
          })
        });
      } else {
        res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (useWebhook) {
        if (res.ok) {
          setEnviado(true);
        } else {
          const data = await res.json().catch(() => ({}));
          setErrorEnvio(data.error || 'No se pudo enviar el pedido. Inténtalo de nuevo o contacta por teléfono.');
        }
      } else {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          setErrorEnvio('El formulario no está conectado al servidor. Comprueba que el archivo send-pedido.php está en la carpeta /api/ del servidor.');
          setEnviando(false);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setEnviado(true);
        } else {
          setErrorEnvio(data.error || 'No se pudo enviar el pedido. Inténtalo de nuevo o contacta por teléfono.');
        }
      }
    } catch (err) {
      setErrorEnvio('Error de conexión. Comprueba tu conexión o contacta por teléfono: +34 822 68 50 12');
    } finally {
      setEnviando(false);
    }
  };

  if (enviado) {
    return (
      <>
        <SEO
          title="Pedido enviado | Recepción de pedidos | E-ASY"
          description="Tu solicitud de pedido ha sido recibida correctamente."
          canonical="https://e-asy.es/pedidos"
        />
        <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <div className={`pedidos-page ${darkMode ? 'dark' : 'light'}`}>
          <section className="pedidos-success">
            <div className="pedidos-success-content">
              <h1 className="pedidos-success-title">Pedido recibido</h1>
              <p className="pedidos-success-text">
                Hemos recibido tu solicitud correctamente. Nos pondremos en contacto contigo en breve para confirmar el pedido.
              </p>
              <a href="/pedidos" className="pedidos-success-link">Enviar otro pedido</a>
            </div>
          </section>
        </div>
        <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      </>
    );
  }

  return (
    <>
      <SEO
        title="Recepción de pedidos | Solicita hardware y software | E-ASY"
        description="Solicita tu pedido de hardware o software desde la web. Formulario de recepción de pedidos para clientes E-ASY. Entrega en tus centros."
        keywords="pedidos, recepción pedidos, solicitar pedido, hardware, software, compra equipos, E-ASY"
        canonical="https://e-asy.es/pedidos"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`pedidos-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="pedidos-hero">
          <div className="pedidos-hero-content">
            <div className="pedidos-hero-text">
              <h1 className="pedidos-hero-title">Recepción de pedidos</h1>
              <p className="pedidos-hero-subtitle">
                Solicita tu pedido de <strong>hardware</strong> o <strong>software</strong> desde la web. 
                Completa el formulario y lo procesaremos lo antes posible. Si antes pedías por teléfono, 
                ahora puedes hacerlo aquí de forma rápida y con toda la información en un solo sitio.
              </p>
              <div className="pedidos-hero-stats">
                <div className="hero-stat">
                  <div className="hero-stat-number">&lt;24h</div>
                  <div className="hero-stat-label">Respuesta</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-number">✓</div>
                  <div className="hero-stat-label">Sin llamadas</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <main className="pedidos-main">
          <div className="pedidos-content">
            <form className="pedidos-form" onSubmit={handleSubmit}>
              <div className="form-header">
                <h2 className="form-title">Solicitar pedido</h2>
                <p className="form-subtitle">
                  Rellena los datos y los artículos que necesitas. Indica para qué los vas a usar y el lugar de entrega.
                </p>
              </div>

              <div className="form-block">
                <h3 className="form-block-title">Datos de contacto</h3>
                <div className="form-row form-row-3">
                  <div className="form-field">
                    <label htmlFor="nombre">Nombre *</label>
                    <input
                      id="nombre"
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="Tu nombre"
                      className={errores.nombre ? 'error' : ''}
                    />
                    {errores.nombre && <span className="field-error">{errores.nombre}</span>}
                  </div>
                  <div className="form-field">
                    <label htmlFor="email">Email *</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className={errores.email ? 'error' : ''}
                    />
                    {errores.email && <span className="field-error">{errores.email}</span>}
                  </div>
                  <div className="form-field">
                    <label htmlFor="telefono">Teléfono *</label>
                    <input
                      id="telefono"
                      type="tel"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      placeholder="+34 600 000 000"
                      className={errores.telefono ? 'error' : ''}
                    />
                    {errores.telefono && <span className="field-error">{errores.telefono}</span>}
                  </div>
                </div>
              </div>

              <div className="form-block">
                <div className="form-block-header">
                  <h3 className="form-block-title">Tipo de compra</h3>
                </div>
                <div className="form-field">
                  <select
                    value={tipoCompra}
                    onChange={(e) => setTipoCompra(e.target.value)}
                    className="form-select"
                  >
                    {TIPO_COMPRA_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-block">
                <div className="form-block-header">
                  <h3 className="form-block-title">¿Qué necesitas?</h3>
                  <button type="button" className="btn-add-line" onClick={addLinea}>
                    + Añadir artículo
                  </button>
                </div>
                {errores.lineas && <span className="field-error block-error">{errores.lineas}</span>}
                <div className="lineas-table-wrap">
                  <table className="lineas-table">
                    <thead>
                      <tr>
                        <th>Artículo</th>
                        <th className="col-cantidad">Cantidad</th>
                        <th>¿Para qué lo vas a usar?</th>
                        <th className="col-action"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((linea) => (
                        <tr key={linea.id}>
                          <td>
                            <input
                              type="text"
                              value={linea.articulo}
                              onChange={(e) => updateLinea(linea.id, 'articulo', e.target.value)}
                              placeholder="ej. portátil, pantalla, licencia Adobe..."
                            />
                          </td>
                          <td className="col-cantidad">
                            <input
                              type="number"
                              min="1"
                              value={linea.cantidad}
                              onChange={(e) => updateLinea(linea.id, 'cantidad', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={linea.uso}
                              onChange={(e) => updateLinea(linea.id, 'uso', e.target.value)}
                              placeholder="ej. para reuniones por Teams, para editar vídeo..."
                            />
                          </td>
                          <td className="col-action">
                            <button
                              type="button"
                              className="btn-remove-line"
                              onClick={() => removeLinea(linea.id)}
                              title="Quitar línea"
                              disabled={lineas.length <= 1}
                            >
                              −
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-block form-row-2">
                <div className="form-field">
                  <label htmlFor="lugarEntrega">Lugar de entrega</label>
                  <select
                    id="lugarEntrega"
                    value={lugarEntrega}
                    onChange={(e) => setLugarEntrega(e.target.value)}
                    className="form-select"
                  >
                    {LUGAR_ENTREGA_OPTIONS.map(lug => (
                      <option key={lug} value={lug}>{lug}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="prioridad">Prioridad</label>
                  <select
                    id="prioridad"
                    value={prioridad}
                    onChange={(e) => setPrioridad(e.target.value)}
                    className="form-select"
                  >
                    {PRIORIDAD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {prioridad === 'urgente' && (
                <div className="form-block">
                  <div className="form-field">
                    <label htmlFor="motivoUrgencia">Motivo de la urgencia *</label>
                    <input
                      id="motivoUrgencia"
                      type="text"
                      value={motivoUrgencia}
                      onChange={(e) => setMotivoUrgencia(e.target.value)}
                      placeholder="Indica por qué es urgente"
                      className={errores.motivoUrgencia ? 'error' : ''}
                    />
                    {errores.motivoUrgencia && (
                      <span className="field-error">{errores.motivoUrgencia}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="form-block">
                <div className="form-field">
                  <label htmlFor="observaciones">Observaciones</label>
                  <textarea
                    id="observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="ej. lo necesita antes del lunes, tiene que ser portátil, teclado en español..."
                    rows={4}
                  />
                </div>
              </div>

              {errorEnvio && (
                <div className="form-error-envio" role="alert">
                  {errorEnvio}
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-submit" disabled={enviando}>
                  {enviando ? 'Enviando…' : 'Enviar pedido'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default PedidosPage;
